import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {firstValueFrom, from} from 'rxjs';
import { first } from 'rxjs/operators';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { SourceBibApiService, SwalService, UIExportService } from '@atmi/core';
import { SourceBibliographic, SourceBibliographicFilter, SourceBibliographicFilterMeta } from '@atmi/core/models';
import { SettingsApiService } from '@atmi/core/services/settings-api.service';
import { IndexQueryStringType } from '@atmi/core/utils';
import {
  SourceBibliographicFiltersComponent,
} from '@atmi/shared/filters/source-bibliographic-filters/source-bibliographic-filters.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-sources-bibliographic',
  styleUrls: ['./sources-bibliographic.component.scss'],
  templateUrl: './sources-bibliographic.component.html',
})
export class SourcesBibliographicComponent
  extends BaseTableFilteredComponent<SourceBibliographic, SourceBibliographicFilter>
  implements UiTableComponent<SourceBibliographic> {

  readonly MobileFiltersComponent = SourceBibliographicFiltersComponent;

  readonly fields: TableField<SourceBibliographic>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      header: this.translate.instant('Titolo'),
      key: 'titolo',
      sortable: true,
    },
    {
      header: this.translate.instant('Tipo'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'tipo',
      sortable: true,
    },
    {
      header: this.translate.instant('Codice Unico'),
      key: 'codiceUnico',
    },
    {
      context: (x) => x.autore && x.autore.join(', '),
      header: this.translate.instant('Autore'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'autore',
    },
    {
      header: this.translate.instant('Anno Edizione'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'annoEdizione',
      sortable: true,
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      template: 'date-time',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  currentFilters: IndexQueryStringType = {};

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) => {
    sort = sort && sort.length > 0 ? sort : [{ field: 'titolo', type: 'asc' }];
    this.currentFilters = { sort, filter };
    return this.sourceBibApi.index({ page, limit, sort, filter });
  });

  readonly contextMenus: Array<TableContextMenu<SourceBibliographic>> = [];

  readonly isExportEnable = this.settingsApi.selectStateSnapshot().system?.layouts.some((l) => l.className === 'DocumentoBib');

  constructor(
    private readonly roleService: RoleService,
    private readonly settingsApi: SettingsApiService,
    private readonly sourceBibApi: SourceBibApiService,
    private readonly uiExportService: UIExportService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, SourceBibliographicFilterMeta, swalService, '/bibs-sources/manager');

    if (this.roleService.hasCapability('DocumentoBib', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('DocumentoBib', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('DocumentoBib', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.sourceBibApi.remove(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = ids.map((id) => firstValueFrom(this.sourceBibApi.remove(id)));

    return from(Promise.all(deleteApis));
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'DocumentoBib');
    if (success) {
      this.selectedItems = [];
    }
  }

  async exportHandler() {
    this.uiExportService.manageExport(
      async (template?: string) => {
        const selection = this.selectedItemIds.length ? this.selectedItemIds : undefined;
        const blob = await firstValueFrom(this.sourceBibApi.exportList(template, this.currentFilters, selection).pipe(first()));
        this.selectedItems = [];
        return blob;
      },
      'DocumentoBib',
      `docs-bib`,
    );
  }
}
